import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowRight, ArrowDown, Check, ChevronDown, Mic, MicOff, Pause, Play,
  ShieldCheck, Wallet, Gem, Clapperboard, Upload, X, Trash2, Search,
  FileAudio, Sparkles, Users, CircleDollarSign, CalendarCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TalentAuth from '../components/TalentAuth';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  supabase, supabaseConfigured, usePlatformReachable, checkPlatformReachable, getPlatformFailReason,
  getSession, saveProfile, submitFallbackApplication,
  fetchCastingCount, fetchTeasers, fetchMyProfile,
  type SampleFile, type TalentProfile, type CastingTeaser,
} from '../lib/talentPlatform';
import {
  LANGUAGES, VOICE_GENDERS, AGE_RANGES, EXPERIENCE_LEVELS, PRO_CATEGORIES,
  COMFORT_OPTIONS, AVAILABILITY_OPTIONS, HEARD_FROM, RECORD_TOPICS, EMAIL_RE,
  Field, TextInput, Pill, VoiceRecorder, UploadZone,
} from '../components/talent/TalentFormKit';

/* ══════════════════════════════════════════════════════════════
   /voice-talent — cinematic recruiting page for the Voqals
   talent network. Two tracks (professional voices / everyday
   speakers), multi-step application with voice sample upload or
   in-browser recording, stored in Supabase (email fallback).
   ══════════════════════════════════════════════════════════════ */

/* ── Scroll helper (same container pattern as the other pages) ── */
const scrollToId = (id: string) => {
  const container = document.getElementById('main-scroll-container');
  const target = document.getElementById(id);
  if (!container || !target) return;
  let top = 0;
  let el: HTMLElement | null = target;
  while (el && el !== container) {
    top += el.offsetTop;
    el = el.offsetParent as HTMLElement | null;
  }
  container.scrollTo({ top, behavior: 'smooth' });
};


/* FAQ — copy verbatim from the 27 Jul 2026 design handoff (locked) */
const FAQS: { q: string; a: string }[] = [
  {
    q: 'What happens after I apply?',
    a: "You build one Voqals voice profile. We review your samples, languages, range and performance strengths. When a suitable project comes in, we contact you with the brief, usage, rate and consent terms before anything is booked. Applying does not mean you've accepted any job.",
  },
  {
    q: 'Do I have to accept every project?',
    a: 'No. You see the brief, usage, rate and consent terms before you say yes. Nothing moves forward without your approval.',
  },
  {
    q: 'Can my voice be cloned?',
    a: 'Not unless you separately agree to it in writing. Speech data and voice licensing are not the same thing — if a project involves your voice being licensed, cloned or used beyond the recording session, that is clearly stated before you book. No hidden consent. No surprise usage.',
  },
  {
    q: 'How does payment work?',
    a: "The rate is agreed before you book. It depends on the project, language, format, session length, usage and rights required. We don't promise one standard hourly rate, because serious AI voice work is not one-size-fits-all. What we do promise: the rate is clear before you record, and payment terms are locked before the session.",
  },
  {
    q: 'What is performance training data?',
    a: 'Not just your voice texture — how you perform. Emotion, pauses, code-switching, emphasis, laughter, rhythm, accents and character choices all teach voice systems how human speech works. That data is valuable, and artists should be paid for it.',
  },
  {
    q: 'Can my voice keep earning after a session?',
    a: 'Sometimes, yes. Some projects are paid recording sessions; others may involve licensing your voice for specific uses. If licensing is part of a project, the terms, duration, usage and payment are discussed separately before you agree. Your voice is never licensed silently.',
  },
  {
    q: 'Why not just take random AI gigs?',
    a: "The best work rarely appears as public casting calls. Serious AI companies work through trusted teams that can cast, direct, record, manage consent and deliver clean data. Random gigs may underpay, skip consent, or take rights you don't fully understand. Enter through the right door.",
  },
  {
    q: 'Do I need my own studio?',
    a: "Not necessarily. Most projects record in directed studio sessions; some allow high-quality remote recording. The project brief tells you what's needed before you book.",
  },
];


/* ══════════════════════════════════════════════════════════════
   Beat 2 — Why Now: pinned scrollytelling, three scenes.
   Scene 1 language grid lights with scroll; scene 2 draws the
   volume-over-time curve; scene 3 plays the NDA proof.
   ══════════════════════════════════════════════════════════════ */
const WHY_LANGS = ['Hindi', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Kannada', 'Malayalam', 'Gujarati', 'Punjabi', 'Odia', 'Assamese', 'Bhojpuri', 'Urdu', 'Rajasthani', 'Haryanvi', 'Konkani', 'Maithili', 'Santali', 'Kashmiri', 'Tulu', 'Manipuri', 'Nepali', 'Dogri', 'Sindhi'];
const CURVE_PATH = 'M 20 278 C 140 272, 240 240, 320 150 C 370 92, 400 62, 430 60 C 470 58, 520 110, 590 160 C 660 205, 720 225, 780 235';

const WhyNowSection = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [scene, setScene] = useState(0);
  const [langCount, setLangCount] = useState(0);
  const [curveDraw, setCurveDraw] = useState(0);
  const [pathLen, setPathLen] = useState(1200);

  useEffect(() => {
    if (pathRef.current) setPathLen(pathRef.current.getTotalLength());
  }, []);

  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    const wrap = wrapRef.current;
    if (!container || !wrap) return;
    const onScroll = () => {
      const r = wrap.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      if (total <= 0) return;
      const p = Math.min(1, Math.max(0, -r.top / total));
      const idx = Math.min(2, Math.floor(p * 3));
      const sp = Math.min(1, Math.max(0, p * 3 - idx));
      setScene(idx);
      setLangCount(idx > 0 ? 24 : Math.round(sp * 24));
      setCurveDraw(idx > 1 ? 1 : idx === 1 ? Math.min(1, sp * 1.25) : 0);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  const sceneCls = (i: number) =>
    `absolute inset-0 flex flex-col items-center justify-center px-[6vw] text-center transition-all duration-500 ${
      scene === i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
    }`;

  return (
    <div ref={wrapRef} className="relative w-full shrink-0" style={{ height: '420vh' }}>
      <div className="sticky top-0 h-screen overflow-hidden flex items-center justify-center">
        <div className="absolute w-[50vw] h-[40vh] top-[30%] left-[25%] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(86,207,252,.09), transparent 70%)', filter: 'blur(60px)' }} />
        <span className="absolute top-[max(76px,9vh)] left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.18em] uppercase text-[#cfe9ff] backdrop-blur-md z-10">
          Why Now
        </span>

        {/* Scene 1 — the scale */}
        <div className={sceneCls(0)}>
          <h3 className="text-[clamp(24px,3.4vw,44px)] font-black uppercase tracking-tight leading-[1.05] max-w-[900px]">
            Every Indian language is being<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">captured at scale.</span>
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 md:gap-2.5 w-[min(860px,90vw)] mt-6 md:mt-10">
            {WHY_LANGS.map((l, i) => (
              <div
                key={l}
                className={`py-2 md:py-2.5 px-1.5 rounded-xl border text-[11px] md:text-[12.5px] font-semibold transition-all duration-300 ${
                  i < langCount
                    ? 'border-[#56CFFC]/60 text-[#eaf6ff] bg-[#1B36FF]/20 shadow-[0_0_18px_rgba(86,207,252,0.25)]'
                    : 'border-white/10 text-white/35'
                }`}
              >
                {l}{i < langCount && <span className="text-[#56CFFC] text-[8px] align-[2px]"> ●</span>}
              </div>
            ))}
          </div>
          <div className="mt-4 md:mt-6 text-[11px] tracking-[0.2em] uppercase text-[#56CFFC] font-bold">{langCount} / 24 in capture</div>
          <p className="mt-4 md:mt-6 max-w-[600px] text-white/60 text-[clamp(12.5px,1.1vw,16px)] leading-relaxed">
            Every AI system that speaks an Indian language learns it from recorded human performance.
            That recording is happening now — at scale.
          </p>
        </div>

        {/* Scene 2 — the peak */}
        <div className={sceneCls(1)}>
          <h3 className="text-[clamp(24px,3.4vw,44px)] font-black uppercase tracking-tight leading-[1.05] max-w-[900px]">
            New use cases will keep coming.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">The volume won't.</span>
          </h3>
          <div className="relative w-[min(820px,92vw)] mt-8">
            <svg viewBox="0 0 800 300" fill="none" className="w-full h-auto overflow-visible">
              <path
                ref={pathRef}
                d={CURVE_PATH}
                stroke="url(#why-curve-grad)"
                strokeWidth="4"
                strokeLinecap="round"
                style={{ strokeDasharray: pathLen, strokeDashoffset: pathLen * (1 - curveDraw), transition: 'stroke-dashoffset 0.15s linear' }}
              />
              <defs>
                <linearGradient id="why-curve-grad" x1="0" y1="0" x2="800" y2="0" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#56CFFC" /><stop offset="1" stopColor="#1B36FF" />
                </linearGradient>
              </defs>
            </svg>
            <div
              className={`absolute text-center transition-all duration-500 ${curveDraw > 0.52 ? 'opacity-100' : 'opacity-0 scale-90'}`}
              style={{ left: '53.7%', top: '2%', transform: 'translateX(-50%)' }}
            >
              <div className="relative w-3.5 h-3.5 rounded-full bg-[#56CFFC] mx-auto mb-2 shadow-[0_0_24px_rgba(86,207,252,0.9)]">
                <span className="absolute -inset-1.5 rounded-full border-2 border-[#56CFFC]/50 animate-ping" />
              </div>
              <b className="text-[clamp(10px,1vw,14px)] tracking-[0.18em] uppercase text-white whitespace-nowrap">You are here</b>
            </div>
            <span className="absolute left-[6%] -bottom-[6%] text-[9px] md:text-[10.5px] tracking-[0.12em] uppercase text-white/45">scale capture begins</span>
            <span className="absolute right-0 bottom-[8%] text-[9px] md:text-[10.5px] tracking-[0.12em] uppercase text-white/45">use cases continue · volume shrinks</span>
          </div>
          <p className="mt-6 md:mt-8 max-w-[600px] text-white/60 text-[clamp(12.5px,1.1vw,16px)] leading-relaxed">
            A language is captured at scale once. After that, the work narrows to top-ups and new
            use cases. Today's volume never comes back.
          </p>
        </div>

        {/* Scene 3 — the proof */}
        <div className={sceneCls(2)}>
          <h3 className="text-[clamp(24px,3.4vw,44px)] font-black uppercase tracking-tight leading-[1.05] max-w-[900px]">
            The biggest projects don't advertise.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">They NDA.</span>
          </h3>
          <div className="flex gap-3.5 flex-wrap justify-center mt-8 md:mt-10">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="relative w-[clamp(120px,14vw,170px)] h-[54px] rounded-2xl border border-white/12 bg-white/[0.05] flex items-center justify-center overflow-hidden">
                <div
                  className="absolute rounded-md"
                  style={{ inset: '8px 40px 8px 14px', background: 'repeating-linear-gradient(90deg, rgba(255,255,255,.22) 0 26px, transparent 26px 34px)', filter: 'blur(5px)' }}
                />
                <span className="relative text-white/40 text-[10px] tracking-[0.2em] uppercase font-semibold">🔒 NDA</span>
              </div>
            ))}
          </div>
          <p className="mt-7 md:mt-9 max-w-[600px] text-white/60 text-[clamp(12.5px,1.1vw,16px)] leading-relaxed">
            No casting calls. No announcements. Serious AI work moves quietly, through people the
            projects trust — that's how you know it's real.
          </p>
          <p className="mt-4 max-w-[640px] text-white/78 text-[clamp(14px,1.25vw,18px)] leading-relaxed">
            <b className="text-white">The team behind Voqals has cast voices for some of the world's biggest AI companies.</b>{' '}
            The talents they cast have earned crores — some of them individually.
          </p>
        </div>

        {/* Progress dots */}
        <div className="absolute bottom-[34px] left-1/2 -translate-x-1/2 flex gap-2">
          {[0, 1, 2].map(i => (
            <span key={i} className={`w-[26px] h-[3px] rounded-full transition-colors duration-300 ${scene === i ? 'bg-[#56CFFC]' : 'bg-white/15'}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   Beat 3 — Straight Answers: live chat with typing indicator.
   Copy verbatim from the handoff (locked).
   ══════════════════════════════════════════════════════════════ */
const CHAT_QA: { q: string; a: string }[] = [
  { q: 'Will my voice be cloned?', a: 'Never without a separate, signed consent. Applying commits you to nothing.' },
  { q: 'Does AI work actually pay?', a: 'Through the right door, yes. The rate is agreed before you book. Paid on time.' },
  { q: 'Am I training my replacement?', a: 'The biggest voice engines were built on performances artists were never paid for. We pay you to record — and if you want, we pitch your voice for licensing on top.' },
  { q: 'So… another casting database?', a: 'No. A curated network. We pitch you project by project, use by use.' },
];

const ChatSection = () => {
  const chatRef = useRef<HTMLDivElement>(null);
  const [shownQ, setShownQ] = useState(0);
  const [shownA, setShownA] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    let played = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const io = new IntersectionObserver(entries => {
      if (played || !entries.some(e => e.isIntersecting)) return;
      played = true;
      io.disconnect();
      let t = 0;
      CHAT_QA.forEach((_, i) => {
        timeouts.push(setTimeout(() => setShownQ(i + 1), t)); t += 650;
        timeouts.push(setTimeout(() => setTyping(true), t)); t += 900;
        timeouts.push(setTimeout(() => { setTyping(false); setShownA(i + 1); }, t)); t += 750;
      });
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); timeouts.forEach(clearTimeout); };
  }, []);

  return (
    <section className="w-full shrink-0 relative px-[6vw] py-[clamp(70px,9vw,140px)] flex flex-col items-center text-center">
      <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.18em] uppercase text-[#cfe9ff] backdrop-blur-md">
        Straight Answers
      </span>
      <h2 className="mt-6 text-[clamp(30px,4.6vw,60px)] font-black uppercase tracking-tighter leading-[0.95]">
        Ask Us Anything.<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">We'll Be Blunt.</span>
      </h2>
      <div ref={chatRef} className="w-full max-w-[680px] mt-14 flex flex-col gap-4 min-h-[380px] text-left">
        {CHAT_QA.map((m, i) => (
          <React.Fragment key={m.q}>
            {shownQ > i && (
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45 }}
                className="self-start max-w-[78%] px-5 py-3.5 rounded-3xl rounded-bl-md bg-white/[0.07] border border-white/10 font-bold text-[clamp(14px,1.15vw,17px)] leading-relaxed"
              >
                {m.q}
              </motion.div>
            )}
            {shownA > i && (
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.45 }}
                className="self-end max-w-[78%] px-5 py-3.5 rounded-3xl rounded-br-md border border-[#56CFFC]/28 text-[#eaf6ff] text-[clamp(14px,1.15vw,17px)] leading-relaxed"
                style={{ background: 'linear-gradient(120deg, rgba(86,207,252,.16), rgba(27,54,255,.28))' }}
              >
                {m.a}
              </motion.div>
            )}
          </React.Fragment>
        ))}
        {typing && (
          <div className="self-end flex gap-1.5 px-5 py-4 rounded-3xl rounded-br-md border border-[#56CFFC]/20" style={{ background: 'linear-gradient(120deg, rgba(86,207,252,.12), rgba(27,54,255,.2))' }}>
            {[0, 1, 2].map(i => (
              <span key={i} className="w-[7px] h-[7px] rounded-full bg-[#eaf6ff]/70 animate-bounce" style={{ animationDelay: `${i * 0.16}s` }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   Beat 4 — Casting Now: live board from Supabase.
   ══════════════════════════════════════════════════════════════ */
// Deadline is a plain date (YYYY-MM-DD) — treat it as inclusive through the end
// of that day in local time. Negative = already passed (the view filters these
// out server-side too, so this is a belt-and-braces guard).
const daysLeft = (deadline: string) => {
  const [y, m, d] = deadline.slice(0, 10).split('-').map(Number);
  const endOfDay = new Date(y, m - 1, d, 23, 59, 59).getTime();
  return Math.ceil((endOfDay - Date.now()) / 86400000);
};
const deadlineLabel = (deadline: string) => {
  const n = daysLeft(deadline);
  if (n < 0) return 'closed';
  if (n === 0) return 'closes today';
  return `closes in ${n} day${n === 1 ? '' : 's'}`;
};

const CastingNowSection = ({ teasers, authed, enabled }: { teasers: CastingTeaser[]; authed: boolean; enabled: boolean }) => {
  if (!enabled) return null;
  return (
    <section id="casting" className="w-full shrink-0 relative px-[6vw] py-[clamp(70px,9vw,140px)] flex flex-col items-center text-center overflow-hidden">
      <div className="absolute w-[44vw] h-[40vh] top-[10%] right-[10%] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(27,54,255,.16), transparent 70%)', filter: 'blur(60px)' }} />
      <span className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.18em] uppercase text-[#cfe9ff] backdrop-blur-md">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        Casting Now
      </span>
      <h2 className="mt-6 text-[clamp(30px,4.6vw,60px)] font-black uppercase tracking-tighter leading-[0.95]">
        Open<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">Right Now.</span>
      </h2>

      {teasers.length === 0 ? (
        <p className="mt-12 max-w-[480px] text-white/60 text-[clamp(13px,1.1vw,16px)] leading-relaxed">
          Nothing is open this second. New castings appear here — one profile covers all of them.
        </p>
      ) : (
        <div className="w-full max-w-[640px] mt-12 flex flex-col gap-5">
          {teasers.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-3xl p-7 md:p-8 text-left border border-[#56CFFC]/22 shadow-[0_24px_60px_rgba(0,0,0,0.5)] transition-all hover:-translate-y-1.5 hover:shadow-[0_34px_80px_rgba(27,54,255,0.35)]"
              style={{ background: 'linear-gradient(160deg, rgba(27,54,255,.12), rgba(255,255,255,.02))' }}
            >
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-emerald-300/35 text-emerald-300 text-[10px] font-bold tracking-[0.15em] uppercase">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </span>
                  Casting
                </span>
                {t.deadline && (
                  <span className="text-amber-200/90 font-bold text-[13px]">
                    {deadlineLabel(t.deadline)}
                  </span>
                )}
              </div>
              <h3 className="mt-4 mb-2 text-[clamp(18px,1.8vw,26px)] font-black uppercase tracking-tight leading-tight">{t.title}</h3>
              <p className="text-white/60 text-[13.5px]">
                {t.city}{t.format && t.format !== 'studio' ? ` · ${t.format}` : ' · Directed studio sessions'} · {t.project_type} · {t.languages.join(' + ')}
              </p>
              {authed ? (
                <Link to="/voice-talent/portal" className="mt-6 inline-flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(86,207,252,0.4)]">
                  Apply via your portal <ArrowRight size={13} />
                </Link>
              ) : (
                <button onClick={() => scrollToId('apply')} className="mt-6 inline-flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(86,207,252,0.4)]">
                  Apply via your profile <ArrowRight size={13} />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}
      {/* Caption only under a populated board — the empty state already says this. */}
      {teasers.length > 0 && (
        <p className="mt-7 text-white/45 text-[13px]">New castings appear here. One profile covers all of them.</p>
      )}
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   Application form — 4 steps
   ══════════════════════════════════════════════════════════════ */
const FORM_STEPS = [
  { num: '01', title: 'The Basics' },
  { num: '02', title: 'Your Voice' },
  { num: '03', title: 'Your Sample' },
  { num: '04', title: 'Final Details' },
];


const ApplicationSection = ({ track, setTrack, session, setSession, authChecked, platformOn, existing, setExisting }: {
  track: 'professional' | 'everyday';
  setTrack: (t: 'professional' | 'everyday') => void;
  session: Session | null;
  setSession: (s: Session | null) => void;
  authChecked: boolean;
  /** Supabase configured AND currently reachable. False → email-only fallback flow. */
  platformOn: boolean;
  existing: (TalentProfile & { status: string }) | null;
  setExisting: (p: (TalentProfile & { status: string }) | null) => void;
}) => {
  const [step, setStep] = useState(0);
  // Step 1
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  // Step 2
  const [languages, setLanguages] = useState<string[]>([]);
  const [langSearch, setLangSearch] = useState('');
  const [voiceGender, setVoiceGender] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [experience, setExperience] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [portfolio, setPortfolio] = useState('');
  const [comfort, setComfort] = useState('');
  // Step 3
  const [sampleTab, setSampleTab] = useState<'record' | 'upload'>('record');
  const [sample, setSample] = useState<SampleFile | null>(null);
  const [topic, setTopic] = useState(RECORD_TOPICS[0]);
  // Step 4
  const [availability, setAvailability] = useState('');
  const [heardFrom, setHeardFrom] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);
  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Sync the locked email with the account
  useEffect(() => {
    if (session) setEmail(session.user.email || '');
  }, [session]);

  const emailValid = EMAIL_RE.test(email);
  const filteredLangs = LANGUAGES.filter(l => l.toLowerCase().includes(langSearch.toLowerCase()));

  const toggle = (list: string[], set: (v: string[]) => void, item: string) =>
    set(list.includes(item) ? list.filter(x => x !== item) : [...list, item]);

  const stepValid = (s: number): boolean => {
    switch (s) {
      case 0: return Boolean(name.trim() && emailValid && city.trim());
      case 1:
        if (!languages.length || !voiceGender || !ageRange) return false;
        return track === 'professional' ? Boolean(experience && categories.length) : Boolean(comfort);
      case 2: return track === 'professional' ? Boolean(sample || existing?.sample_path) : true;
      case 3: return Boolean(availability && consent);
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!stepValid(3) || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const app: TalentProfile = {
      track,
      full_name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      city: city.trim() || null,
      languages,
      voice_gender: voiceGender || null,
      age_range: ageRange || null,
      experience_level: track === 'professional' ? experience || null : null,
      categories: track === 'professional' ? categories : [],
      portfolio_url: track === 'professional' ? portfolio.trim() || null : null,
      comfort: track === 'everyday' ? comfort || null : null,
      availability: availability || null,
      heard_from: heardFrom || null,
      notes: notes.trim() || null,
      consent,
      // Keep the existing stored sample when editing without a new one
      sample_path: existing?.sample_path ?? null,
      sample_source: existing?.sample_source ?? null,
    };
    try {
      if (platformOn && session) {
        await saveProfile(session.user.id, app, sample);
        setExisting({ ...app, status: existing?.status || 'new' });
      } else {
        // Build a diagnostic for the team email: why did this applicant skip
        // the account path? Re-probe now so we can tell "was down at load,
        // fine at submit" apart from "still down".
        let why: string;
        if (!supabaseConfigured) {
          why = 'Supabase not configured in this build';
        } else if (!platformOn) {
          const loadReason = getPlatformFailReason();
          const nowOk = await checkPlatformReachable({ fresh: true }).catch(() => false);
          why = `backend unreachable at page load [${loadReason}]; re-check at submit: ${nowOk ? 'REACHABLE (transient)' : `still failing [${getPlatformFailReason()}]`}`;
        } else {
          why = 'backend reachable but no signed-in session at submit (session expired or auth skipped)';
        }
        await submitFallbackApplication(app, sample, why);
      }
      setSubmitted(true);
      window.gtag?.('event', 'conversion', { send_to: 'AW-18066710209/65G5CK-OyJYcEMG98KZD', value: 1.0, currency: 'INR' });
    } catch (err) {
      console.error('Talent application error:', err);
      setSubmitError('Something went wrong submitting your application. Please try again — or email partners@voqals.com with your details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="w-full shrink-0 relative px-[var(--px-container)] py-[clamp(56px,6vw,120px)]">
      <div className="max-w-[900px] mx-auto">
        <div className="flex flex-col items-center text-center mb-[clamp(28px,2.8vw,52px)]">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md mb-5 md:mb-[clamp(16px,1.4vw,24px)]">
            Apply
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-[length:var(--heading-pipeline)] font-black tracking-tighter leading-[0.92] text-white uppercase">
            One Profile.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">Every Casting.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 md:mt-[clamp(16px,1.4vw,24px)] max-w-[560px] text-white/75 text-[length:var(--text-body)] leading-relaxed">
            Apply once. When a project fits, we pitch you — brief, rate, and consent terms
            before you say yes.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="gradient-border rounded-3xl backdrop-blur-xl shadow-2xl relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 50%, rgba(255,255,255,0.01) 100%), rgba(10,10,10,0.65)' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#1B36FF]/8 blur-[100px] rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#56CFFC]/8 blur-[100px] rounded-full pointer-events-none" />

          {submitted ? (
            <div className="relative z-10 flex flex-col items-center justify-center px-8 py-16 md:py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-6">
                <Check size={28} className="text-[#56CFFC]" />
              </div>
              <h3 className="text-white font-black text-[clamp(22px,2vw,36px)] tracking-tight uppercase mb-3">You're On The Roster</h3>
              <p className="text-white/80 text-[clamp(12px,0.95vw,15px)] leading-relaxed max-w-md">
                Thank you, {name.split(' ')[0] || 'friend'}. Your profile and sample are with our casting team — kept private, never published.
                The moment a project matches your voice, you'll hear from us with the details and the rate.
              </p>
              {platformOn && session && (
                <Link
                  to="/voice-talent/portal"
                  className="mt-7 inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all"
                >
                  Open Your Portal — See What's Casting <ArrowRight size={13} />
                </Link>
              )}
            </div>
          ) : supabaseConfigured && !authChecked ? (
            <div className="relative z-10 flex items-center justify-center py-20 text-white/50 text-sm">Loading…</div>
          ) : platformOn && !session ? (
            <div className="relative z-10 p-6 md:p-[clamp(28px,2.6vw,48px)] max-w-md mx-auto">
              <TalentAuth
                onAuthed={setSession}
                title="Step 1 — Create Your Account"
                subtitle="Your application becomes a living profile: one account for your details, your voice sample, and every casting call. We just email you a login code — no password, ever."
              />
            </div>
          ) : platformOn && session && existing ? (
            <div className="relative z-10 flex flex-col items-center justify-center px-8 py-14 md:py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#56CFFC]/20 to-[#1B36FF]/20 border border-[#56CFFC]/30 flex items-center justify-center mb-5">
                <Check size={24} className="text-[#56CFFC]" />
              </div>
              <h3 className="text-white font-black text-[clamp(20px,1.8vw,32px)] tracking-tight uppercase mb-3">You're Already On The Roster</h3>
              <p className="text-white/75 text-[clamp(12px,0.9vw,15px)] leading-relaxed max-w-md mb-7">
                Signed in as <span className="text-white">{session.user.email}</span> — your profile is with the casting team.
                Check what's casting right now, or update your details and sample any time.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/voice-talent/portal"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] transition-all"
                >
                  Open Your Portal <ArrowRight size={13} />
                </Link>
                <Link
                  to="/voice-talent/portal#profile"
                  className="px-7 py-3 rounded-full border border-white/15 text-white/75 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 hover:text-white transition-colors"
                >
                  Edit Profile
                </Link>
              </div>
            </div>
          ) : (
            <div className="relative z-10 p-5 md:p-[clamp(28px,2.6vw,48px)]">
              {supabaseConfigured && !platformOn && (
                <p className="mb-5 md:mb-6 px-4 py-3 rounded-2xl border border-amber-300/25 bg-amber-300/[0.06] text-amber-100/85 text-[11.5px] leading-relaxed">
                  Talent accounts are temporarily unavailable, so this application goes straight to our casting team by email.
                  Nothing else changes — you'll hear from us the same way.
                </p>
              )}
              {/* Step indicator */}
              <div className="flex items-center gap-2 md:gap-3 mb-6 md:mb-8">
                {FORM_STEPS.map((s, i) => (
                  <React.Fragment key={s.num}>
                    <button
                      type="button"
                      onClick={() => { if (i < step) setStep(i); }}
                      className={`flex items-center gap-2 shrink-0 ${i < step ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <span className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-[10px] md:text-[11px] font-bold border transition-all ${
                        i === step ? 'bg-gradient-to-br from-[#56CFFC] to-[#1B36FF] border-transparent text-white shadow-[0_0_16px_rgba(86,207,252,0.35)]'
                        : i < step ? 'bg-[#56CFFC]/15 border-[#56CFFC]/40 text-[#56CFFC]'
                        : 'bg-white/[0.03] border-white/10 text-white/40'
                      }`}>
                        {i < step ? <Check size={12} /> : s.num}
                      </span>
                      <span className={`hidden md:inline text-[10px] font-bold tracking-[0.15em] uppercase ${i === step ? 'text-white' : i < step ? 'text-[#56CFFC]/80' : 'text-white/35'}`}>
                        {s.title}
                      </span>
                    </button>
                    {i < FORM_STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? 'bg-[#56CFFC]/40' : 'bg-white/10'}`} />}
                  </React.Fragment>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col gap-5"
                >
                  {/* ── STEP 1: The Basics ── */}
                  {step === 0 && (
                    <>
                      <Field label="Which voice are you?">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {([
                            { key: 'professional' as const, title: 'Professional Voice', desc: 'VO artist, dubbing, narration, RJ, singer — the mic is your craft.' },
                            { key: 'everyday' as const, title: 'Everyday Speaker', desc: 'No training needed — your natural voice and language are the asset.' },
                          ]).map(t => (
                            <button
                              key={t.key}
                              type="button"
                              onClick={() => setTrack(t.key)}
                              className={`text-left rounded-2xl border p-4 transition-all ${
                                track === t.key
                                  ? 'border-[#56CFFC]/60 bg-[#56CFFC]/[0.07] shadow-[0_0_20px_rgba(86,207,252,0.12)]'
                                  : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-sm font-bold ${track === t.key ? 'text-white' : 'text-white/80'}`}>{t.title}</span>
                                <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${track === t.key ? 'border-[#56CFFC] bg-[#56CFFC]' : 'border-white/25'}`}>
                                  {track === t.key && <Check size={10} className="text-black" />}
                                </span>
                              </div>
                              <span className="text-white/60 text-[11px] leading-snug block">{t.desc}</span>
                            </button>
                          ))}
                        </div>
                      </Field>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Full Name">
                          <TextInput value={name} onChange={setName} placeholder="Aarav Sharma" />
                        </Field>
                        <Field label="Email">
                          <TextInput value={email} onChange={setEmail} placeholder="aarav@email.com" type="email"
                            disabled={Boolean(session)}
                            hint={session ? 'Your verified account email' : undefined}
                            error={email && !emailValid ? 'Please enter a valid email.' : undefined} />
                        </Field>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Phone / WhatsApp" optional>
                          <TextInput value={phone} onChange={setPhone} placeholder="+91 98765 43210" type="tel" />
                        </Field>
                        <Field label="City">
                          <TextInput value={city} onChange={setCity} placeholder="Mumbai" />
                        </Field>
                      </div>
                    </>
                  )}

                  {/* ── STEP 2: Your Voice ── */}
                  {step === 1 && (
                    <>
                      <Field label="Languages you speak fluently">
                        <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] border border-white/10 rounded-xl mb-2.5">
                          <Search size={13} className="text-gray-500 shrink-0" />
                          <input
                            type="text"
                            value={langSearch}
                            onChange={e => setLangSearch(e.target.value)}
                            placeholder="Search languages…"
                            className="bg-transparent text-white text-sm focus:outline-none w-full placeholder:text-gray-600"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {filteredLangs.map(lang => (
                            <Pill key={lang} selected={languages.includes(lang)} onClick={() => toggle(languages, setLanguages, lang)}>{lang}</Pill>
                          ))}
                        </div>
                        {languages.length > 0 && (
                          <span className="text-[#56CFFC]/80 text-[10px] mt-1">{languages.length} selected — code-switchers, pick every language you mix.</span>
                        )}
                      </Field>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Voice / Gender">
                          <div className="flex flex-wrap gap-2">
                            {VOICE_GENDERS.map(g => <Pill key={g} selected={voiceGender === g} onClick={() => setVoiceGender(g)}>{g}</Pill>)}
                          </div>
                        </Field>
                        <Field label="Age Range">
                          <div className="flex flex-wrap gap-2">
                            {AGE_RANGES.map(a => <Pill key={a} selected={ageRange === a} onClick={() => setAgeRange(a)}>{a}</Pill>)}
                          </div>
                        </Field>
                      </div>

                      {track === 'professional' ? (
                        <>
                          <Field label="Years behind the mic">
                            <div className="flex flex-wrap gap-2">
                              {EXPERIENCE_LEVELS.map(x => <Pill key={x} selected={experience === x} onClick={() => setExperience(x)}>{x}</Pill>)}
                            </div>
                          </Field>
                          <Field label="What do you do best?">
                            <div className="flex flex-wrap gap-2">
                              {PRO_CATEGORIES.map(c => <Pill key={c} selected={categories.includes(c)} onClick={() => toggle(categories, setCategories, c)}>{c}</Pill>)}
                            </div>
                          </Field>
                          <Field label="Portfolio / reel link" optional>
                            <TextInput value={portfolio} onChange={setPortfolio} placeholder="YouTube, Drive, SoundCloud, website…" type="url" />
                          </Field>
                        </>
                      ) : (
                        <Field label="What are you comfortable with?">
                          <div className="flex flex-wrap gap-2">
                            {COMFORT_OPTIONS.map(c => <Pill key={c} selected={comfort === c} onClick={() => setComfort(c)}>{c}</Pill>)}
                          </div>
                        </Field>
                      )}
                    </>
                  )}

                  {/* ── STEP 3: Your Sample ── */}
                  {step === 2 && (
                    <>
                      <div className="flex flex-col gap-1">
                        <span className="text-[9px] md:text-[clamp(8px,0.64vw,11px)] uppercase tracking-widest text-white/80 font-medium">
                          Your voice sample {track === 'everyday' && <span className="text-white/40 normal-case tracking-normal ml-1">(Optional — but it moves you up the queue)</span>}
                        </span>
                        <p className="text-white/60 text-[11px] leading-snug">
                          {track === 'professional'
                            ? 'Upload your reel — or record here: AI casting values natural, unscripted speech as much as a polished demo. Pick a topic and just talk for 2 minutes.'
                            : "Nothing fancy, no script. Pick a topic, hit record, and talk naturally for up to 2 minutes in the language(s) you're applying with — exactly how you'd talk to a friend."}
                        </p>
                      </div>

                      {/* Record / Upload tabs */}
                      <div className="flex gap-2">
                        {([['record', 'Record Here', <Mic key="m" size={13} />], ['upload', 'Upload File', <Upload key="u" size={13} />]] as const).map(([key, label, icon]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setSampleTab(key)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-bold tracking-[0.15em] uppercase border transition-all ${
                              sampleTab === key
                                ? 'bg-[#56CFFC]/12 border-[#56CFFC]/60 text-[#56CFFC]'
                                : 'border-white/10 text-white/55 hover:text-white hover:border-white/30'
                            }`}
                          >
                            {icon} {label}
                          </button>
                        ))}
                      </div>

                      {sampleTab === 'record' ? (
                        <>
                          {/* Talking topic picker — natural monologue is what AI needs */}
                          <div className="flex flex-col gap-2">
                            <span className="text-white/60 text-[10px] uppercase tracking-widest font-medium">Pick a topic to talk about</span>
                            <div className="flex flex-wrap gap-2">
                              {RECORD_TOPICS.map(t => (
                                <Pill key={t} selected={topic === t} onClick={() => setTopic(t)}>{t}</Pill>
                              ))}
                            </div>
                            <div className="rounded-xl border border-[#56CFFC]/20 bg-[#1B36FF]/[0.06] px-4 py-3">
                              <span className="text-white/85 text-[12px] leading-relaxed">
                                <span className="text-[#56CFFC] font-bold">Your topic: </span>
                                {topic} — no script, no preparation. Rambling, pausing, and switching languages mid-sentence are all
                                perfect. That's the "natural speech" AI pays for.
                              </span>
                            </div>
                          </div>
                          <VoiceRecorder sample={sample} onRecorded={setSample} onClear={() => setSample(null)} />
                        </>
                      ) : (
                        <UploadZone sample={sample} onSelected={setSample} onClear={() => setSample(null)} />
                      )}

                      {sample && sample.source === (sampleTab === 'record' ? 'upload' : 'recorded') && (
                        <p className="text-white/60 text-[11px]">
                          ✓ You already added a {sample.source === 'upload' ? 'file' : 'recording'} — switching tabs won't lose it.
                        </p>
                      )}
                      {!sample && existing?.sample_path && (
                        <p className="text-[#56CFFC]/80 text-[11px]">
                          ✓ You already have a sample on file — record or upload only if you want to replace it.
                        </p>
                      )}
                    </>
                  )}

                  {/* ── STEP 4: Final Details ── */}
                  {step === 3 && (
                    <>
                      <Field label="When are you generally available to record?">
                        <div className="flex flex-wrap gap-2">
                          {AVAILABILITY_OPTIONS.map(a => <Pill key={a} selected={availability === a} onClick={() => setAvailability(a)}>{a}</Pill>)}
                        </div>
                      </Field>
                      <Field label="How did you hear about us?" optional>
                        <div className="flex flex-wrap gap-2">
                          {HEARD_FROM.map(h => <Pill key={h} selected={heardFrom === h} onClick={() => setHeardFrom(heardFrom === h ? '' : h)}>{h}</Pill>)}
                        </div>
                      </Field>
                      <Field label="Anything else we should know?" optional>
                        <TextInput value={notes} onChange={setNotes} placeholder="Accents you do, scheduling constraints, special skills…" />
                      </Field>
                      <button
                        type="button"
                        onClick={() => setConsent(!consent)}
                        className={`text-left flex items-start gap-3 rounded-2xl border p-4 transition-all ${
                          consent ? 'border-[#56CFFC]/50 bg-[#56CFFC]/[0.05]' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 mt-0.5 transition-all ${consent ? 'border-[#56CFFC] bg-[#56CFFC]' : 'border-white/25'}`}>
                          {consent && <Check size={12} className="text-black" />}
                        </span>
                        <span className="text-white/75 text-[11px] md:text-[12px] leading-relaxed">
                          I consent to Voqals storing my details and voice sample to evaluate me for paid voice work.
                          My sample stays private, and my voice is never used to train anything without a separate,
                          signed project agreement.
                        </span>
                      </button>
                      {submitError && <p className="text-red-400/90 text-[11px] leading-snug">{submitError}</p>}
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="flex items-center justify-between mt-7 md:mt-9">
                {step > 0 ? (
                  <button type="button" onClick={() => setStep(step - 1)}
                    className="px-5 py-2.5 rounded-full border border-white/10 text-white/70 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-white/5 hover:text-white transition-colors">
                    Back
                  </button>
                ) : <span />}
                {step < FORM_STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => stepValid(step) && setStep(step + 1)}
                    disabled={!stepValid(step)}
                    className="group relative px-7 py-2.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-35 disabled:hover:shadow-none flex items-center gap-2"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                    <span className="relative z-10 flex items-center gap-2">Continue <ArrowRight size={13} /></span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!stepValid(3) || submitting}
                    className="group relative px-7 py-2.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-35 disabled:hover:shadow-none flex items-center gap-2"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                    <span className="relative z-10 flex items-center gap-2">
                      {submitting ? 'Submitting…' : 'Submit Application'} {!submitting && <ArrowRight size={13} />}
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Everyday-speaker line — the only mention on the page (per handoff) */}
        <p className="mt-6 text-center text-white/55 text-[13px] md:text-[13.5px]">
          Not a professional? Apply anyway. Natural voices get cast too.
        </p>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   FAQ
   ══════════════════════════════════════════════════════════════ */
const FaqSection = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="w-full shrink-0 relative px-[var(--px-container)] py-[clamp(48px,5vw,110px)]">
      <div className="max-w-[820px] mx-auto">
        <div className="flex flex-col items-center text-center mb-[clamp(28px,2.8vw,52px)]">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md mb-5 md:mb-[clamp(16px,1.4vw,24px)]">
            FAQ
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-[length:var(--heading-pipeline)] font-black tracking-tighter leading-[0.92] text-white uppercase">
            The Detail<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">, Out Loud.</span>
          </motion.h2>
        </div>
        <div className="flex flex-col gap-2.5 md:gap-3">
          {FAQS.map((f, i) => (
            <div key={f.q} className={`rounded-2xl border transition-colors ${open === i ? 'border-[#56CFFC]/30 bg-white/[0.03]' : 'border-white/10 bg-white/[0.015]'}`}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className={`text-[13px] md:text-[clamp(13px,1vw,17px)] font-bold ${open === i ? 'text-white' : 'text-white/80'}`}>{f.q}</span>
                <ChevronDown size={16} className={`text-[#56CFFC] shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-5 text-white/70 text-[12px] md:text-[clamp(12px,0.9vw,15px)] leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════
   Page
   ══════════════════════════════════════════════════════════════ */
export default function VoiceTalentPage() {
  const [track, setTrack] = useState<'professional' | 'everyday'>('professional');
  const [castingCount, setCastingCount] = useState<number | null>(null);
  const [teasers, setTeasers] = useState<CastingTeaser[]>([]);
  // Account state lives at page level so every CTA can adapt to it
  const [session, setSession] = useState<Session | null>(null);
  const [authChecked, setAuthChecked] = useState(!supabaseConfigured);
  const [existing, setExisting] = useState<(TalentProfile & { status: string }) | null>(null);
  // Backend health: null while checking, false when the Supabase project is paused/offline.
  // While false, account features hide and the form falls back to the email-only flow.
  const reachable = usePlatformReachable();
  const platformOn = supabaseConfigured && reachable === true;

  // Live casting board data (anon-visible teaser view)
  useEffect(() => {
    if (!supabaseConfigured) return;
    let cancelled = false;
    fetchCastingCount().then(c => { if (!cancelled) setCastingCount(c); });
    fetchTeasers().then(t => { if (!cancelled) setTeasers(t); });
    return () => { cancelled = true; };
  }, []);

  // Auth bootstrap (magic-link redirects handled by supabase-js)
  useEffect(() => {
    if (!supabaseConfigured || !supabase) return;
    let cancelled = false;
    getSession().then(s => { if (!cancelled) { setSession(s); setAuthChecked(true); } });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => { if (!cancelled) setSession(s); });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  // Load the account's profile so CTAs know roster membership
  useEffect(() => {
    if (!session) { setExisting(null); return; }
    let cancelled = false;
    fetchMyProfile().then(p => { if (!cancelled && p) setExisting(p); });
    return () => { cancelled = true; };
  }, [session]);

  // Hash navigation (e.g. /voice-talent/#apply from the portal or ads)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      scrollToId(hash);
      history.replaceState(null, '', window.location.pathname);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  usePageMeta({
    title: 'AI Voice Work for Indian Artists — Apply to Voqals',
    description:
      'AI is learning every Indian language from real performances. Voqals casts those voices — and pitches them to the biggest projects in the world. Brief, rate, and consent terms before you say yes.',
    canonicalUrl: 'https://voqals.com/voice-talent/',
    ogTitle: 'The Voice Gold Rush Is Now — Voqals',
    ogDescription:
      'AI is learning every Indian language from real performances. Voqals casts those voices — and pitches them to the biggest projects in the world.',
    ogImage: 'https://voqals.com/og-image.png',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://voqals.com/' },
          { '@type': 'ListItem', position: 2, name: 'Voice Talent', item: 'https://voqals.com/voice-talent/' },
        ],
      },
    ],
  });

  useEffect(() => {
    if (window.location.hash) return; // hash effect handles positioning
    window.scrollTo(0, 0);
    document.getElementById('main-scroll-container')?.scrollTo(0, 0);
  }, []);

  return (
    <div
      id="main-scroll-container"
      className="h-[100dvh] w-full overflow-x-hidden overflow-y-scroll overscroll-y-contain no-scrollbar bg-[#04060F] text-white selection:bg-[#56CFFC]/35 font-sans relative flex flex-col"
    >
      {/* ── Background — deep navy-black with layered glow depth (per handoff) ── */}
      <div className="fixed top-[10%] left-[15%] w-[55vw] h-[45vh] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(27,54,255,.18), transparent 70%)', filter: 'blur(100px)' }} />
      <div className="fixed bottom-[5%] right-[5%] w-[40vw] h-[35vh] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(86,207,252,.08), transparent 70%)', filter: 'blur(100px)' }} />
      <div className="fixed inset-0 bg-[url('/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none z-0" />

      {/* ── Navigation (shared) ── */}
      <Navbar onScrollToId={scrollToId} />

      {/* ══════════ HERO ══════════ */}
      <section id="hero" className="min-h-[100dvh] w-full shrink-0 relative flex flex-col items-center justify-center px-[var(--px-container)] pt-24 pb-10 z-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[70%] bg-gradient-to-br from-[#1B36FF]/10 to-[#56CFFC]/5 blur-[120px] rounded-full pointer-events-none animate-pulse-slow" />

        <div className="relative z-10 flex flex-col items-center text-center max-w-[980px] mx-auto">
          <div className="animate-hero-fade-in inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B36FF]/10 border border-[#56CFFC]/30 text-[9px] md:text-[clamp(8px,0.69vw,12px)] font-bold tracking-[0.15em] uppercase backdrop-blur-md shadow-[0_0_20px_rgba(86,207,252,0.15)] mb-6 md:mb-[clamp(20px,1.8vw,32px)]">
            <Mic size={11} className="text-[#56CFFC]" /> The Voqals Talent Network
          </div>

          <h1 className="animate-hero-fade-in text-[length:var(--heading-hero)] font-black tracking-tighter leading-[0.95] text-white uppercase" style={{ animationDelay: '0.08s' }}>
            The Voice Gold Rush<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] italic">Is Now.</span>
          </h1>

          <p className="animate-hero-fade-in mt-5 md:mt-[clamp(20px,1.8vw,32px)] max-w-[640px] text-white/80 text-[length:var(--text-body)] leading-relaxed" style={{ animationDelay: '0.16s' }}>
            AI is learning every Indian language from real performances. Voqals casts those
            voices — and pitches them to the biggest projects in the world.
          </p>

          {castingCount !== null && castingCount > 0 && (
            <button
              onClick={() => scrollToId('casting')}
              className="animate-hero-fade-in mt-5 md:mt-[clamp(18px,1.6vw,28px)] inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-300 text-[10px] md:text-[clamp(9px,0.72vw,12px)] font-bold tracking-[0.15em] uppercase hover:border-emerald-400/60 hover:bg-emerald-400/10 transition-all"
              style={{ animationDelay: '0.2s' }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              {castingCount} Project{castingCount === 1 ? '' : 's'} Casting Now
            </button>
          )}

          <div className="animate-hero-fade-in flex flex-col sm:flex-row items-center gap-3 md:gap-4 mt-7 md:mt-[clamp(28px,2.5vw,44px)]" style={{ animationDelay: '0.24s' }}>
            {session && existing ? (
              <Link to="/voice-talent/portal"
                className="group relative px-8 py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] md:text-[clamp(9px,0.75vw,13px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(86,207,252,0.4)] flex items-center gap-2">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                <span className="relative z-10 flex items-center gap-2">Open Your Portal <ArrowRight size={14} /></span>
              </Link>
            ) : (
              <button onClick={() => scrollToId('apply')}
                className="group relative px-8 py-3.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] md:text-[clamp(9px,0.75vw,13px)] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(86,207,252,0.4)] flex items-center gap-2">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                <span className="relative z-10 flex items-center gap-2">{session ? 'Complete Your Profile' : 'Apply to Voqals'} <ArrowRight size={14} /></span>
              </button>
            )}
            {platformOn && (
              <button onClick={() => scrollToId('casting')}
                className="px-8 py-3.5 rounded-full border border-white/15 text-white/85 text-[10px] md:text-[clamp(9px,0.75vw,13px)] font-bold tracking-[0.2em] uppercase hover:bg-white/5 hover:border-white/35 transition-all flex items-center gap-2">
                See casting <ArrowDown size={13} />
              </button>
            )}
          </div>

          {platformOn && !session && (
            <Link
              to="/voice-talent/portal"
              className="animate-hero-fade-in mt-4 text-white/55 hover:text-[#56CFFC] text-[11px] md:text-[clamp(10px,0.75vw,13px)] transition-colors underline-offset-2 hover:underline"
              style={{ animationDelay: '0.28s' }}
            >
              Already on the roster? Log in to your portal
            </Link>
          )}

        </div>
      </section>

      {/* ══════════ SECTIONS ══════════ */}
      <WhyNowSection />
      <ChatSection />
      <CastingNowSection teasers={teasers} authed={Boolean(session)} enabled={platformOn} />
      <ApplicationSection
        track={track}
        setTrack={setTrack}
        session={session}
        setSession={setSession}
        authChecked={authChecked && reachable !== null}
        platformOn={platformOn}
        existing={existing}
        setExisting={setExisting}
      />
      <FaqSection />

      {/* ══════════ FOOTER ══════════ */}
      <Footer />
    </div>
  );
}
